import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ArbitragePage.css";
import {
  classifySportForAnalysisTab,
  formatAnalysisTime,
  formatAnalysisOddsDisplay,
  getAnalysisStatusLabel,
  getAnalysisTranslations,
  translateLeagueDisplay,
  translateMarketDisplay,
  translateMarketDisplayWithPeriodSplit,
  translateSelectionDisplay,
  buildArbitrageMiddleMarketRowLabel,
} from "./analysisTranslate";
import { normalizeSharpMarketKeyForDisplay } from "./analysisDataMaps";
import { isSuspiciousEpochLikeMs } from "./analysisTimeUtils";

// ─── Shared odds format sync ───────────────────────────────────
const ANALYSIS_ODDS_FORMAT_KEY = "analysis_options_odds_format";
const VALID_ODDS_FORMATS = ["hk", "decimal", "american"];

function readOddsFormatFromStorage() {
  try {
    const v = localStorage.getItem(ANALYSIS_ODDS_FORMAT_KEY);
    if (v && VALID_ODDS_FORMATS.includes(v.toLowerCase())) return v.toLowerCase();
  } catch { /* ignore */ }
  return "hk";
}

function useOddsFormat() {
  const [oddsFormat, setOddsFormat] = React.useState(readOddsFormatFromStorage);
  React.useEffect(() => {
    function sync() {
      const next = readOddsFormatFromStorage();
      setOddsFormat((prev) => (prev !== next ? next : prev));
    }
    // Cross-tab sync (storage event fires in other windows)
    window.addEventListener("storage", sync);
    // Same-window sync via custom event dispatched by settings page
    window.addEventListener("analysis_odds_format_changed", sync);
    // Fallback poll every 300ms — handles same-window SPA tab switching
    const iv = setInterval(sync, 300);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("analysis_odds_format_changed", sync);
      clearInterval(iv);
    };
  }, []);
  return oddsFormat;
}
// ───────────────────────────────────────────────────────────────

const MIDDLES_DEBUG_PREFIX = "[MIDDLES DEBUG]";

function getMiddlesDebugTarget() {
  try {
    if (typeof window === "undefined") return "";
    const v = window.__MIDDLES_DEBUG_TARGET__;
    return v == null ? "" : String(v).trim();
  } catch {
    return "";
  }
}

function isMiddlesDebugTargetMatch(rawItem, target) {
  const t = String(target || "").trim();
  if (!t) return false;
  try {
    const sid = getItemStableIdentityKey(rawItem);
    if (sid && sid === t) return true;
  } catch {
    /* ignore */
  }
  const id = rawItem && rawItem.id != null ? String(rawItem.id).trim() : "";
  if (id && id === t) return true;
  return false;
}

function getMiddlesDebugKeyFromItem(it) {
  const sid = getItemStableIdentityKey(it);
  if (sid) return sid;
  const id = it && it.id != null ? String(it.id).trim() : "";
  return id;
}

function pickMiddlesDebugFields(rawItem) {
  const item = rawItem && typeof rawItem === "object" ? rawItem : null;
  if (!item) return { _missing: true };
  const stableKey = item.stableKey || item.opportunityFingerprint || item.semanticOpportunityFingerprint;
  const match =
    item.match ||
    item.matchName ||
    item.eventName ||
    item.event_name ||
    item.fixtureName ||
    item.name ||
    item.title ||
    "";
  const rawLegs = resolveRawLegs(item);
  return {
    id: item.id,
    stableKey: stableKey,
    opportunityFingerprint: item.opportunityFingerprint,
    match: match,
    event_name: item.event_name,
    market: item.market,
    line: item.line ?? item.point ?? item.handicap ?? item.total,
    side1: item.side1,
    side2: item.side2,
    picks: item.picks,
    legs: item.legs,
    rawResolvedLegs: rawLegs,
  };
}

let cachedAnalysisApiBase = null;

async function detectAnalysisApiBase() {
  if (cachedAnalysisApiBase !== null) return cachedAnalysisApiBase;
  const candidates = ["http://localhost:3001", ""];
  for (const base of candidates) {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 4000);
      const res = await fetch(`${base}/api/_selftest`, { signal: ctrl.signal });
      clearTimeout(t);
      if (!res.ok) continue;
      const j = await res.json().catch(() => null);
      if (j && j.ok === true) {
        cachedAnalysisApiBase = base;
        return base;
      }
    } catch {
      /* next */
    }
  }
  cachedAnalysisApiBase = "";
  return "";
}

function httpBaseToAnalysisWsUrl(base) {
  try {
    const b = String(base ?? "").trim();
    if (!b) {
      if (typeof window === "undefined") return "ws://localhost:3001/api/analysis/ws";
      const p = window.location.protocol === "https:" ? "wss:" : "ws:";
      return `${p}//${window.location.host}/api/analysis/ws`;
    }
    const u = new URL(b, typeof window !== "undefined" ? window.location.href : "http://localhost");
    const wsP = u.protocol === "https:" ? "wss:" : "ws:";
    return `${wsP}//${u.host}/api/analysis/ws`;
  } catch {
    return null;
  }
}

function mergeRawMiddlesLists(liveList, earlyList) {
  const map = new Map();
  let anon = 0;
  for (const it of earlyList || []) {
    const k =
      String(it?.stableKey ?? it?.opportunityFingerprint ?? it?.id ?? "").trim() ||
      `__mid_${anon++}`;
    map.set(k, it);
  }
  for (const it of liveList || []) {
    const k =
      String(it?.stableKey ?? it?.opportunityFingerprint ?? it?.id ?? "").trim() ||
      `__mid_${anon++}`;
    map.set(k, it);
  }
  return Array.from(map.values());
}

const MIDDLE_PCT_OPTIONS = [1, 2, 3, 5, 8, 10, 15];

function clampToNearestNumberOption(value, options) {
  const opts = Array.isArray(options) ? options : [];
  if (!opts.length) return value;
  const v = Number(value);
  if (!Number.isFinite(v)) return opts[0];
  let best = opts[0];
  let bestDiff = Math.abs(opts[0] - v);
  for (let i = 1; i < opts.length; i += 1) {
    const opt = opts[i];
    const diff = Math.abs(opt - v);
    if (diff < bestDiff) {
      best = opt;
      bestDiff = diff;
    } else if (diff === bestDiff && opt > best) {
      best = opt;
      bestDiff = diff;
    }
  }
  return best;
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  return [];
}

function toNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function normalizeScope(value) {
  const raw = String(value || "").toLowerCase();
  if (raw === "live") return "live";
  if (raw === "prematch" || raw === "pre-match" || raw === "early") return "prematch";
  return "prematch";
}

function leagueStringFromUnknown(v) {
  if (v == null) return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  if (typeof v === "object") {
    return String(
      v.name || v["@name"] || v.league_name || v.leagueName || v.title || v.label || v.competition || ""
    ).trim();
  }
  return "";
}

function buildMatchLabel(item) {
  const direct =
    item?.match ||
    item?.matchName ||
    item?.eventName ||
    item?.fixtureName ||
    item?.name ||
    item?.title ||
    "";
  if (String(direct).trim()) return String(direct).trim();
  const home =
    item?.homeTeamName ||
    item?.homeTeam ||
    item?.teams?.home?.name ||
    item?.event?.homeTeam ||
    item?.participants?.[0]?.name ||
    "";
  const away =
    item?.awayTeamName ||
    item?.awayTeam ||
    item?.teams?.away?.name ||
    item?.event?.awayTeam ||
    item?.participants?.[1]?.name ||
    "";
  if (home && away) return `${home} vs ${away}`;
  return String(home || away || "").trim();
}

function getItemSport(item) {
  const raw =
    item?.sport ??
    item?.sportKey ??
    item?.sportName ??
    item?.category ??
    item?.gameType ??
    "";
  const match =
    item?.match ??
    item?.matchName ??
    item?.eventName ??
    item?.event_name ??
    "";
  return classifySportForAnalysisTab(raw, { league: item?.league, match });
}

function resolveRawLegs(item) {
  const candidates = [
    toArray(item?.legs),
    toArray(item?.picks),
    toArray(item?.selections),
    toArray(item?.outcomes),
  ];
  for (const legs of candidates) {
    if (legs.length > 0) return legs;
  }
  const sharpSides = [item?.side1, item?.side2].filter(Boolean);
  if (sharpSides.length > 0) return sharpSides;
  return [];
}

function resolvePickBook(rawPick) {
  return (
    rawPick?.book ||
    rawPick?.bookmaker ||
    rawPick?.bookmakerName ||
    rawPick?.bookName ||
    rawPick?.operator ||
    rawPick?.site ||
    rawPick?.source ||
    ""
  );
}

function resolvePickSelection(rawPick) {
  return (
    rawPick?.sel ||
    rawPick?.selection ||
    rawPick?.selectionName ||
    rawPick?.outcome ||
    rawPick?.label ||
    rawPick?.name ||
    ""
  );
}

function isValidDecimalOdds(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 1;
}

function isValidAmericanOdds(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return false;
  if (n === 0) return false;
  return true;
}

function americanToDecimal(value) {
  if (!isValidAmericanOdds(value)) return null;
  const a = Number(value);
  const dec = a >= 0 ? 1 + a / 100 : 1 + 100 / Math.abs(a);
  return isValidDecimalOdds(dec) ? dec : null;
}

function resolvePickOdds(rawPick) {
  const oddsField = rawPick?.odds;

  // 1) If odds is a direct decimal number/string, use it.
  if (
    (typeof oddsField === "number" || typeof oddsField === "string") &&
    isValidDecimalOdds(oddsField)
  ) {
    return Number(oddsField);
  }

  // 1) If odds is a nested object (Sharp-style), prefer decimal, else convert american.
  if (oddsField && typeof oddsField === "object") {
    const nestedDecimal = oddsField?.decimal;
    if (isValidDecimalOdds(nestedDecimal)) return Number(nestedDecimal);

    const nestedAm = oddsField?.american;
    const nestedAmConverted = americanToDecimal(nestedAm);
    if (isValidDecimalOdds(nestedAmConverted)) return nestedAmConverted;
  }

  // 2) Explicit decimal fallback
  if (isValidDecimalOdds(rawPick?.odds_decimal)) return Number(rawPick.odds_decimal);

  // 3) Explicit american fallback
  const amConverted = americanToDecimal(rawPick?.odds_american);
  if (isValidDecimalOdds(amConverted)) return amConverted;

  return null;
}

function normalizeLeg(rawPick) {
  const book = String(resolvePickBook(rawPick) || "").trim();
  const sel = String(resolvePickSelection(rawPick) || "").trim();
  const odds = resolvePickOdds(rawPick);
  const line =
    rawPick?.line ??
    rawPick?.lineA ??
    rawPick?.lineB ??
    rawPick?.overLine ??
    rawPick?.underLine ??
    rawPick?.handicap ??
    rawPick?.total ??
    rawPick?.point ??
    "";
  const out = {
    sel,
    odds,
    book,
    line: line == null ? "" : String(line).trim(),
    selection: rawPick?.selection != null ? rawPick.selection : sel,
    odds_decimal: rawPick?.odds_decimal,
    odds_american: rawPick?.odds_american,
    implied_probability: rawPick?.implied_probability,
    fair_probability: rawPick?.fair_probability,
    stake_percent: rawPick?.stake_percent,
    odds_age_seconds: rawPick?.odds_age_seconds,
  };
  return out;
}

function getResponseItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload?.middles)) return payload.middles;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
}

function isMeaningfulLineForCard(line) {
  const s = line == null ? "" : String(line).trim();
  if (!s) return false;
  if (s === "—" || s === "-") return false;
  return true;
}

const TOTAL_MARKET_KEYS = new Set([
  "total",
  "totals",
  "total_points",
  "total_goals",
  "total_runs",
  "total_games",
  "team_total",
  "over/under",
  "over_under",
  "overunder",
]);

const TOTAL_SELECTION_RE = /^(over|under|tài|xỉu)$/i;

function isTotalLikeMarket(marketKey, selLabel) {
  const mk = String(marketKey ?? "").toLowerCase().trim();
  if (mk && TOTAL_MARKET_KEYS.has(mk)) return true;
  // also catch keys that start with "total" (e.g. "total_corners")
  if (mk.startsWith("total")) return true;
  // check selection label: Over/Under/Tài/Xỉu are total-side selections
  const sel = String(selLabel ?? "").toLowerCase().trim();
  if (TOTAL_SELECTION_RE.test(sel)) return true;
  return false;
}

function formatMiddleLineForDisplay(value, marketKey, selLabel) {
  const raw = value == null ? "" : String(value).trim();
  if (!raw || raw === "—" || raw === "-") return "";
  const isTotal = isTotalLikeMarket(marketKey, selLabel);
  if (isTotal) {
    // strip leading + for total markets; keep – for negative (shouldn't occur but guard anyway)
    return raw.replace(/^\+/, "");
  }
  // handicap/spread: keep existing signed behaviour
  if (/^[+-]/.test(raw)) return raw;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return `+${raw}`;
  return raw;
}

function normalizeLegSelectionForDisplay(sel) {
  const s = String(sel ?? "").trim();
  if (!s) return { baseForTranslate: "", extractedLine: "" };
  const m = /^(Over|Under)\s+([-+]?\d+(?:\.\d+)?)\s*$/i.exec(s);
  if (m) {
    const canonical = /^over$/i.test(m[1]) ? "Over" : "Under";
    return { baseForTranslate: canonical, extractedLine: m[2] };
  }
  return { baseForTranslate: s, extractedLine: "" };
}

function normalizeMiddleMarketForTranslate(rawMarket) {
  const raw = String(rawMarket ?? "").trim();
  if (!raw) return "";
  const key = normalizeSharpMarketKeyForDisplay(rawMarket);
  switch (key) {
    case "total goals":
    case "goals total":
      return "total goals";
    case "total points":
    case "points total":
    case "total rounds":
    case "rounds total":
    case "over/under":
      return "Totals";
    case "spread":
      return "spread";
    case "point spread":
      return "point spread";
    case "run line":
      return "run line";
    case "team total":
    case "team totals":
      return "team total";
    case "team total goals":
      return "team total goals";
    case "total runs":
      return "total runs";
    case "total games":
      return "total games";
    case "set handicap":
      return "set handicap";
    case "game handicap":
      return "game handicap";
    case "asian handicap":
      return "Asian Handicap";
    case "moneyline":
      return "Moneyline";
    case "1x2":
      return "1X2";
    case "full time result":
    case "fulltime result":
    case "full-time result":
      return "1X2";
    case "corner match bet":
    case "asian handicap corners":
    case "corners asian handicap":
      return key;
    case "set moneyline":
      return "set moneyline";
    case "puck line":
      return "puck line";
    case "player made threes":
      return "player made threes";
    default:
      return key || raw;
  }
}

function selectionAlreadyContainsLine(sel, line) {
  const rawSel = String(sel ?? "").trim();
  const rawLine = String(line ?? "").trim();
  if (!rawSel || !rawLine) return false;
  if (!isMeaningfulLineForCard(rawLine)) return false;
  const lineCore = rawLine.replace(/\s+/g, "");
  if (!lineCore) return false;
  const escaped = lineCore.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(^|[^0-9.,])${escaped}($|[^0-9])`, "i");
  return re.test(rawSel);
}

function getEsportsLeagueLabel(item, lang = "en-GB") {
  const rawLeagueTop = String(item?.league || "").trim();
  const ev = item?.event || item?.fixture?.event;
  const rawLeagueNested = !rawLeagueTop
    ? leagueStringFromUnknown(item?.fixture?.league) ||
      leagueStringFromUnknown(item?.fixture?.leagueName) ||
      leagueStringFromUnknown(item?.fixture?.competition) ||
      leagueStringFromUnknown(item?.fixture?.competitionName) ||
      leagueStringFromUnknown(item?.fixture?.tournamentName) ||
      leagueStringFromUnknown(ev?.league) ||
      leagueStringFromUnknown(ev?.leagueName) ||
      leagueStringFromUnknown(ev?.competition) ||
      leagueStringFromUnknown(ev?.competitionName) ||
      ""
    : "";
  const rawLeague = rawLeagueTop || rawLeagueNested;
  const translated = translateLeagueDisplay(rawLeague, lang);
  const displayLeague = translated || rawLeague || "—";
  const game = String(item?.game || item?.gameName || "").trim();
  if (!translated && !rawLeague && !game) return "—";
  if (displayLeague === "—" && !game) return "—";
  if (!game) return displayLeague;
  if (displayLeague === "—") return game || "—";
  return String(`${displayLeague} · ${game}` || "").trim() || "—";
}

function getEsportsMarketLabel(item, lang = "en-GB") {
  const rawMarket = String(item?.market || "").trim();
  const canonicalMarket = normalizeMiddleMarketForTranslate(item?.market);
  const market =
    translateMarketDisplayWithPeriodSplit(rawMarket, lang) ||
    translateMarketDisplay(canonicalMarket, lang) ||
    canonicalMarket ||
    rawMarket;
  const map = String(item?.map || item?.mapName || "").trim();
  if (!market) return map;
  if (!map) return market;
  return `${market} · ${map}`;
}

function getClassicMarketRowLabel(item, lang = "en-GB") {
  return buildArbitrageMiddleMarketRowLabel(item, lang);
}

function isUsableKickoffTs(value) {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return false;
  const a = Math.abs(value);
  let ms;
  if (a >= 1e12) ms = value;
  else if (a >= 1e9) ms = Math.round(value * 1000);
  else return false;
  return !isSuspiciousEpochLikeMs(ms);
}

function isSuspiciousKickoffDisplayText(s) {
  const t = String(s || "").trim();
  if (!t) return true;
  if (/1970|01\s*Jan|Jan\s*01|01\/01\/1970|1970-01-01|01\.01\.1970/i.test(t)) return true;
  return false;
}

function isUsableKickoffString(value) {
  const t = String(value ?? "").trim();
  if (!t) return false;
  return !isSuspiciousKickoffDisplayText(t);
}

function getItemStableIdentityKey(item) {
  const stableKey =
    item && (item.stableKey || item.opportunityFingerprint || item.semanticOpportunityFingerprint);
  const fallbackId = item && item.id;
  return String(stableKey || fallbackId || "");
}

function resolveMiddlePct(item) {
  const v =
    item?.middlePct ??
    item?.middlePercent ??
    item?.profitPct ??
    item?.edgePct ??
    item?.roiPct ??
    item?.windowPct ??
    item?.roiPercent;
  return toNumber(v, NaN);
}

function resolveLastUpdateTsMs(item) {
  const candidates = [item?.lastUpdateTs, item?.lastUpdate, item?.ts, item?.updatedAt];
  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i];
    if (typeof c === "number" && Number.isFinite(c) && c > 0) {
      const a = Math.abs(c);
      if (a >= 1e12) return c;
      if (a >= 1e9) return Math.round(c * 1000);
    }
    if (typeof c === "string" && c.trim()) {
      const ms = Date.parse(c.trim());
      if (Number.isFinite(ms) && !isSuspiciousEpochLikeMs(ms)) return ms;
    }
  }
  return null;
}

function normalizeMiddleItem(item, fallbackScope) {
  const rawLegs = resolveRawLegs(item);
  const legs = rawLegs.map((leg) => normalizeLeg(leg)).filter((leg) => leg.book || leg.sel || leg.odds);

  const sport =
    item?.sport ||
    item?.sportKey ||
    item?.sportName ||
    item?.category ||
    item?.gameType ||
    "";

  const fxLeague = item?.fixture?.league;
  const fx = item?.fixture || {};
  const info = item?.info || item?.core || item?.meta || fx.info || fx.core || fx.meta;
  const ev = item?.event || item?.fixture?.event;
  const evLeagueStr =
    (ev &&
      (typeof ev.leagueName === "string"
        ? ev.leagueName.trim()
        : typeof ev.league === "string"
          ? ev.league.trim()
          : ev.league && typeof ev.league === "object"
            ? String(ev.league.name || ev.league["@name"] || "").trim()
            : "")) ||
    (typeof ev?.competition === "string" ? ev.competition.trim() : "") ||
    (typeof ev?.competitionName === "string" ? ev.competitionName.trim() : "") ||
    (typeof ev?.tournamentName === "string" ? ev.tournamentName.trim() : "");
  const leagueObjStr =
    leagueStringFromUnknown(item?.league) ||
    leagueStringFromUnknown(fxLeague) ||
    leagueStringFromUnknown(info?.league) ||
    leagueStringFromUnknown(info?.competition) ||
    leagueStringFromUnknown(info?.tournament);
  const league =
    leagueObjStr ||
    item?.leagueName ||
    item?.competition ||
    item?.competitionName ||
    item?.tournament ||
    item?.tournamentName ||
    item?._sourceLeague ||
    (typeof fxLeague === "string" ? fxLeague : "") ||
    (fxLeague && typeof fxLeague === "object"
      ? String(fxLeague.name || fxLeague["@name"] || "").trim()
      : "") ||
    item?.group?.name ||
    item?.division?.name ||
    leagueStringFromUnknown(item?.group) ||
    leagueStringFromUnknown(item?.division) ||
    item?.fixture?.leagueName ||
    item?.fixture?.competition ||
    item?.fixture?.competitionName ||
    item?.fixture?.tournamentName ||
    leagueStringFromUnknown(fx.group) ||
    leagueStringFromUnknown(fx.division) ||
    item?.category?.name ||
    item?.event?.leagueName ||
    evLeagueStr ||
    "";

  const usableKickoffTs = isUsableKickoffTs(item?.kickoffTs);
  let kickoffDateStr = "";
  if (typeof item?.kickoffDate === "string" && isUsableKickoffString(item.kickoffDate)) {
    kickoffDateStr = item.kickoffDate.trim();
  } else if (usableKickoffTs) {
    const a = Math.abs(item.kickoffTs);
    const ms = a >= 1e12 ? item.kickoffTs : Math.round(item.kickoffTs * 1000);
    try {
      kickoffDateStr = new Date(ms).toISOString();
    } catch {
      kickoffDateStr = "";
    }
  } else if (typeof item?.kickoff === "string" && isUsableKickoffString(item.kickoff)) {
    kickoffDateStr = item.kickoff.trim();
  } else {
    const ev2 = item?.event || fx.event || {};
    const fallbackKo =
      item?.startTime ??
      item?.startsAt ??
      item?.commenceTime ??
      item?.kickoff ??
      fx.date ??
      fx.dateTime ??
      fx.datetime ??
      fx.kickoff ??
      fx.kickoffDate ??
      ev2.dateTime ??
      ev2.startsAt ??
      "";
    if (fallbackKo != null && fallbackKo !== "") {
      const fs = String(fallbackKo);
      if (isUsableKickoffString(fs)) kickoffDateStr = fs;
    }
  }

  const market =
    item?.market ||
    item?.marketType ||
    item?.marketName ||
    item?.betType ||
    item?.lineLabel ||
    "";

  const line = item?.line ?? item?.point ?? item?.handicap ?? item?.total ?? "";
  const map = item?.map || item?.mapName || item?.round || "";
  const game = item?.game || item?.gameName || item?.esportGame || "";

  const middlePct = resolveMiddlePct(item);
  const middlePctSafe = Number.isFinite(middlePct) ? middlePct : 0;

  const id =
    item?.id ||
    item?.eventId ||
    item?.fixtureId ||
    item?.matchId ||
    `${buildMatchLabel(item)}-${market}-${String(line)}-${normalizeScope(item?.scope || fallbackScope)}`;

  const matchLabel =
    (item?.match && String(item.match).trim()) ||
    buildMatchLabel(item) ||
    "—";

  const scopeResolved = normalizeScope(
    item?.scope != null && item?.scope !== ""
      ? item.scope
      : item?.isLive
        ? "live"
        : fallbackScope
  );

  const evTz = item?.event || item?.fixture?.event;
  const timeZoneResolved =
    (typeof item?.timeZone === "string" && item.timeZone.trim()) ||
    (typeof item?.timezone === "string" && item.timezone.trim()) ||
    (typeof item?.tz === "string" && item.tz.trim()) ||
    (item?.fixture && item.fixture.timezone) ||
    (item?.fixture && item.fixture.timeZone) ||
    (item?.event && item.event.timeZone) ||
    (evTz && typeof evTz.timeZone === "string" && evTz.timeZone.trim()) ||
    "Asia/Ho_Chi_Minh";

  const windowParts = [
    item?.middleWindow,
    item?.middleRange,
    item?.winZone,
    item?.pushZone,
    item?.corridor,
    item?.gap,
  ]
    .map((x) => (x == null ? "" : String(x).trim()))
    .filter(Boolean);

  const lastUpdateTsMs = resolveLastUpdateTsMs(item);

  const next = {
    ...item,
    id: String(id),
    scope: scopeResolved,
    sport: String(sport),
    league: String(league || "").trim(),
    kickoffDate: kickoffDateStr,
    kickoff: kickoffDateStr,
    match: matchLabel,
    middlePct: middlePctSafe,
    market: String(market || "").trim(),
    line: line == null ? "" : String(line),
    map: String(map || "").trim(),
    game: String(game || "").trim(),
    legs,
    middleMetaLine: windowParts.join(" · ") || "",
  };

  if (usableKickoffTs) {
    next.kickoffTs = item.kickoffTs;
  } else {
    delete next.kickoffTs;
  }

  if (timeZoneResolved) {
    next.timeZone = String(timeZoneResolved);
  }

  if (lastUpdateTsMs != null) {
    next.lastUpdateTs = lastUpdateTsMs;
  }

  return next;
}

function itemIsLiveScope(item) {
  const scope = normalizeScope(item?.scope);
  if (scope === "live") return true;
  if (item?.isLive === true) return true;
  return false;
}

function filterVisibleMiddles(items, sport, scope, pctFilter) {
  return toArray(items)
    .filter((item) => getItemSport(item) === sport)
    .filter((item) => {
      if (scope === "all") return true;
      return normalizeScope(item?.scope) === scope;
    })
    .filter((item) => toNumber(item?.middlePct, 0) >= toNumber(pctFilter, 0))
    .slice()
    .sort((a, b) => {
      const liveA = itemIsLiveScope(a);
      const liveB = itemIsLiveScope(b);
      if (liveA !== liveB) return liveA ? -1 : 1;
      const ua = typeof a?.lastUpdateTs === "number" ? a.lastUpdateTs : 0;
      const ub = typeof b?.lastUpdateTs === "number" ? b.lastUpdateTs : 0;
      if (liveA && liveB && ua !== ub) return ub - ua;
      const pctDiff = toNumber(b?.middlePct, 0) - toNumber(a?.middlePct, 0);
      if (pctDiff !== 0) return pctDiff;
      return String(a?.id || "").localeCompare(String(b?.id || ""));
    });
}

function fmtTime(ts, lang = "en-GB", timeZone) {
  return formatAnalysisTime(ts, lang, timeZone);
}

function isBadCardTimeDisplay(str) {
  if (!str || str === "—") return true;
  if (/1970|01\s*Jan|Jan\s*01|1970-01-01/i.test(str)) return true;
  return false;
}

function parseMiddleMatchParts(match) {
  const s = String(match ?? "").trim();
  if (s.includes(" @ ")) {
    const idx = s.indexOf(" @ ");
    return { left: s.slice(0, idx).trim(), right: s.slice(idx + 3).trim(), separatorType: "@" };
  }
  const vsMatch = / vs /i.exec(s);
  if (vsMatch) {
    return { left: s.slice(0, vsMatch.index).trim(), right: s.slice(vsMatch.index + vsMatch[0].length).trim(), separatorType: "vs" };
  }
  const vMatch = / v /i.exec(s);
  if (vMatch) {
    return { left: s.slice(0, vMatch.index).trim(), right: s.slice(vMatch.index + vMatch[0].length).trim(), separatorType: "v" };
  }
  if (s.includes(" - ")) {
    const idx = s.indexOf(" - ");
    return { left: s.slice(0, idx).trim(), right: s.slice(idx + 3).trim(), separatorType: "-" };
  }
  return { left: s, right: "", separatorType: "" };
}

function getMiddleHomeTeamName(item) {
  const explicit =
    (item?.homeTeamName && String(item.homeTeamName).trim()) ||
    (item?.homeTeam && String(item.homeTeam).trim()) ||
    (item?.teams?.home?.name && String(item.teams.home.name).trim()) ||
    (item?.event?.homeTeam && String(item.event.homeTeam).trim()) ||
    (item?.fixture?.homeTeam && String(item.fixture.homeTeam).trim()) ||
    (item?.fixture?.teams?.home?.name && String(item.fixture.teams.home.name).trim()) ||
    "";
  if (explicit) return explicit;
  const match = String(item?.match ?? "").trim();
  if (!match || match === "—") return "";
  const { left, right, separatorType } = parseMiddleMatchParts(match);
  if (separatorType === "@") return right;
  if (left) return left;
  return match;
}

function renderMiddleMatchWithCopy(item, copiedHome, onCopy) {
  const match = String(item?.match ?? "—").trim();
  const homeName = getMiddleHomeTeamName(item);
  const { left, right, separatorType } = parseMiddleMatchParts(match);
  const hasSeparator = Boolean(separatorType && (left || right));
  const displayLeft = hasSeparator ? left : match;
  const displayRight = hasSeparator ? right : "";
  const showCopyBtn = homeName && homeName !== "—";

  return (
    <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap" }}>
      {hasSeparator ? (
        <>
          <span>{displayLeft}</span>
          <span style={{ margin: "0 4px", color: "#7d89a8" }}>-</span>
          <span>{displayRight}</span>
        </>
      ) : (
        <span>{displayLeft}</span>
      )}
      {showCopyBtn ? (
        <button
          type="button"
          title={copiedHome ? "Đã copy" : `Copy ${homeName}`}
          aria-label={copiedHome ? "Đã copy" : `Copy ${homeName}`}
          onClick={(e) => onCopy(e, homeName)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 18,
            height: 18,
            marginLeft: 5,
            padding: 0,
            border: copiedHome ? "1px solid rgba(34,197,94,0.35)" : "1px solid rgba(180,197,235,0.18)",
            borderRadius: 4,
            background: copiedHome ? "rgba(34,197,94,0.12)" : "rgba(76,111,255,0.08)",
            color: copiedHome ? "#4ade80" : "#a8b3cf",
            fontSize: 10,
            cursor: "pointer",
            verticalAlign: "middle",
            lineHeight: 1,
            flexShrink: 0,
            transition: "color 0.15s, background 0.15s, border-color 0.15s",
          }}
        >
          {copiedHome ? "✓" : "📋"}
        </button>
      ) : null}
    </span>
  );
}

function formatDisplayOdds(value, fmt = "hk") {
  return formatAnalysisOddsDisplay(value, fmt);
}

const MiddleCard = React.memo(function MiddleCard({ item, lang, timeZone, t, oddsFormat = "hk" }) {
  const didDebugLogRef = useRef(false);
  const [copiedHome, setCopiedHome] = useState(false);
  const copyResetTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
    };
  }, []);

  const handleCopyHomeTeam = async (e, homeName) => {
    e.preventDefault();
    e.stopPropagation();
    if (!homeName) return;
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(homeName);
      } else {
        const ta = document.createElement("textarea");
        ta.value = homeName;
        ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedHome(true);
      if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
      copyResetTimerRef.current = setTimeout(() => setCopiedHome(false), 1000);
    } catch {
      /* ignore */
    }
  };
  const statusInfo = getAnalysisStatusLabel(item, t, lang, "—", timeZone);
  const isEsports = getItemSport(item) === "esports";
  const rawLeagueTop = String(item?.league || "").trim();
  const ev = item?.event || item?.fixture?.event;
  const rawLeagueNested = !rawLeagueTop
    ? leagueStringFromUnknown(item?.fixture?.league) ||
      leagueStringFromUnknown(item?.fixture?.leagueName) ||
      leagueStringFromUnknown(item?.fixture?.competition) ||
      leagueStringFromUnknown(ev?.league) ||
      ""
    : "";
  const rawLeague = rawLeagueTop || rawLeagueNested;
  const displayLeague = translateLeagueDisplay(rawLeague, lang) || rawLeague || "—";

  let displayTimeValue = statusInfo.timeValue;
  if (statusInfo.isLive) {
    if (isBadCardTimeDisplay(displayTimeValue)) {
      displayTimeValue = "LIVE";
    }
  } else {
    if (isBadCardTimeDisplay(displayTimeValue)) {
      displayTimeValue = "—";
    }
    if (!displayTimeValue || displayTimeValue === "—") {
      const rawKickoff = item?.kickoffDate ?? item?.kickoff ?? "";
      if (isUsableKickoffString(rawKickoff)) {
        displayTimeValue = formatAnalysisTime(rawKickoff, lang, timeZone);
      } else if (isUsableKickoffTs(item?.kickoffTs)) {
        const a = Math.abs(item.kickoffTs);
        const ms = a >= 1e12 ? item.kickoffTs : Math.round(item.kickoffTs * 1000);
        displayTimeValue = formatAnalysisTime(ms, lang, timeZone);
      } else {
        displayTimeValue = "—";
      }
    }
    if (isBadCardTimeDisplay(displayTimeValue)) {
      displayTimeValue = "—";
    }
  }

  let updatedLabel = "";
  const updatedTs =
    typeof item.lastUpdateTs === "number" && Number.isFinite(item.lastUpdateTs) && item.lastUpdateTs > 0
      ? item.lastUpdateTs
      : null;
  if (updatedTs != null) {
    try {
      updatedLabel = fmtTime(updatedTs, lang, timeZone);
    } catch {
      updatedLabel = "";
    }
  }

  const legs = toArray(item?.legs).slice(0, 2);
  const showMiddleBadge = Number.isFinite(item?.middlePct) && toNumber(item.middlePct, 0) > 0;
  const middleMeta = String(item?.middleMetaLine || "").trim();
  const stableId = getItemStableIdentityKey(item);

  const debugTarget = getMiddlesDebugTarget();
  if (debugTarget && !didDebugLogRef.current && isMiddlesDebugTargetMatch(item, debugTarget)) {
    didDebugLogRef.current = true;
    try {
      const legsForDisplay = legs.map((leg) => {
        const { baseForTranslate, extractedLine } = normalizeLegSelectionForDisplay(leg.sel);
        const selectionLabel = translateSelectionDisplay(baseForTranslate, lang) || baseForTranslate || "—";
        const bookLabel = String(leg.book || "").trim() || "—";
        const oddsText = formatDisplayOdds(leg?.odds, oddsFormat);
        const oddsLabel = oddsText ? oddsText : "—";
        let linePart = "";
        if (isMeaningfulLineForCard(leg.line)) {
          linePart = String(leg.line).trim();
        } else if (extractedLine && isMeaningfulLineForCard(extractedLine)) {
          linePart = extractedLine;
        }
        if (linePart && selectionAlreadyContainsLine(selectionLabel, linePart)) {
          linePart = "";
        }
        return {
          sel_raw: leg.sel,
          book_raw: leg.book,
          odds_raw: leg.odds,
          line_raw: leg.line,
          selectionLabel,
          linePart,
          bookLabel,
          oddsText,
          oddsLabel,
          displayedBook: bookLabel,
          displayedOdds: oddsLabel,
        };
      });
      const payload = {
        key: stableId || item?.id,
        id: item?.id,
        stableKey: item?.stableKey || item?.opportunityFingerprint || item?.semanticOpportunityFingerprint,
        match: item?.match || item?.event_name || item?.eventName,
        market: item?.market,
        line: item?.line,
        picks_raw: item?.picks,
        legs_raw: item?.legs,
        resolved_normalized_legs_odds: legsForDisplay.map((x) => ({ odds_raw: x.odds_raw, oddsText: x.oddsText })),
        sportsbook_names_shown: legsForDisplay.map((x) => x.bookLabel),
        displayed_odds_values: legsForDisplay.map((x) => x.oddsLabel),
        rendered: {
          statusInfo,
          displayTimeValue,
          displayLeague,
          updatedLabel,
          middleMeta,
          legsForDisplay,
        },
      };
      console.log(MIDDLES_DEBUG_PREFIX, "4) final rendered card values", payload);
      if (typeof window !== "undefined") {
        window.__MIDDLES_DEBUG_LAST_RENDERED__ = payload;
      }
    } catch (e) {
      console.log(MIDDLES_DEBUG_PREFIX, "4) render debug error", e);
    }
  }

  return (
    <div className="arb-result-card">
      <div className="arb-result-top">
        <div className="arb-result-main">
          <div className="arb-result-league-row">
            <span className="arb-result-league">
              {isEsports ? getEsportsLeagueLabel(item, lang) : displayLeague}
            </span>
            <div
              className={`arb-status-time-pill ${statusInfo.isLive ? "is-live" : "is-prematch"}`}
            >
              <span className="arb-status-time-value">{displayTimeValue}</span>
            </div>
          </div>

          <div className="arb-result-match-row">
            <div className="arb-result-match">{renderMiddleMatchWithCopy(item, copiedHome, handleCopyHomeTeam)}</div>
            {showMiddleBadge ? (
              <div className="arb-result-profit is-profit-flat">
                <span className="arb-result-profit-value">
                  +{toNumber(item.middlePct, 0).toFixed(2)}%
                </span>
              </div>
            ) : null}
          </div>

          {isEsports ? (
            <div className="arb-result-market-row">
              <div className="arb-result-market">{getEsportsMarketLabel(item, lang) || "—"}</div>
              {updatedLabel ? (
                <div className="arb-result-updated-pill">
                  <span className="arb-result-updated-label">{t.common.updated}</span>{" "}
                  <span className="arb-result-updated-time">{updatedLabel}</span>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="arb-result-market-row">
              <div className="arb-result-market">{getClassicMarketRowLabel(item, lang) || "—"}</div>
              {updatedLabel ? (
                <div className="arb-result-updated-pill">
                  <span className="arb-result-updated-label">{t.common.updated}</span>{" "}
                  <span className="arb-result-updated-time">{updatedLabel}</span>
                </div>
              ) : null}
            </div>
          )}

          <div className="arb-result-picks-preview">
            {legs.length ? (
              legs.map((leg, idx) => {
                const { baseForTranslate, extractedLine } = normalizeLegSelectionForDisplay(leg.sel);
                const selectionLabel =
                  translateSelectionDisplay(baseForTranslate, lang) || baseForTranslate || "—";
                const bookLabel = String(leg.book || "").trim() || "—";
                const oddsText = formatDisplayOdds(leg?.odds, oddsFormat);
                const oddsLabel = oddsText ? oddsText : "—";
                let linePart = "";
                if (isMeaningfulLineForCard(leg.line)) {
                  linePart = String(leg.line).trim();
                } else if (extractedLine && isMeaningfulLineForCard(extractedLine)) {
                  linePart = extractedLine;
                }
                if (linePart && selectionAlreadyContainsLine(selectionLabel, linePart)) {
                  linePart = "";
                }
                const displayLinePart = formatMiddleLineForDisplay(linePart, item.market, selectionLabel);
                const previewSel = displayLinePart ? `${selectionLabel} · ${displayLinePart}` : selectionLabel;
                const titleText = [selectionLabel, displayLinePart || null, bookLabel, oddsLabel]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <div key={`${stableId || item.id}-mid-${idx}`} className="arb-pick-preview-row">
                    <div className="arb-pick-preview-sel" title={titleText}>
                      {previewSel}
                    </div>
                    <div className="arb-pick-preview-right">
                      <span className="arb-pill arb-pill-odds">{oddsLabel}</span>
                      <span className="arb-pill arb-pill-book" title={bookLabel}>
                        {bookLabel}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="arb-pick-preview-row is-empty">
                <div className="arb-pick-preview-sel">—</div>
              </div>
            )}
          </div>

          {middleMeta ? (
            <div
              style={{
                marginTop: 6,
                fontSize: 10,
                fontWeight: 500,
                color: "rgba(168, 179, 207, 0.72)",
                lineHeight: 1.3,
              }}
            >
              {middleMeta}
            </div>
          ) : null}
      {(item.is_guaranteed_profit || (Array.isArray(item.key_numbers) && item.key_numbers.length > 0) || (Array.isArray(item.warnings) && item.warnings.length > 0)) && (
        <div className="arb-warning-row arb-card-bottom-warnings">
          {item.is_guaranteed_profit && (
            <span className="arb-warning-badge arb-warning-stale" title={`Lợi nhuận đảm bảo tối thiểu ${item.guaranteed_roi != null ? item.guaranteed_roi.toFixed(2) + "%" : ""}`}>
              ✅ {t.middles.guaranteedProfit}{item.guaranteed_roi != null ? ` ${item.guaranteed_roi.toFixed(2)}%` : ""}
            </span>
          )}
          {Array.isArray(item.key_numbers) && item.key_numbers.length > 0 && (
            <span className="arb-warning-badge arb-warning-live" title="Số then chốt trong vùng middle">
              🎯 {t.middles.keyNumbers}: {item.key_numbers.join(", ")}{item.key_number_probability != null ? ` (${(item.key_number_probability * 100).toFixed(1)}%)` : ""}
            </span>
          )}
          {Array.isArray(item.warnings) && item.warnings.includes("STALE_ODDS") && (
            <span className="arb-warning-badge arb-warning-stale" title="Odds có thể đã cũ">⚠ {t.middles.staleOdds}</span>
          )}
          {Array.isArray(item.warnings) && item.warnings.includes("LARGE_GAP") && (
            <span className="arb-warning-badge arb-warning-suspicious" title="Khoảng cách line lớn, kiểm tra lại">⚠ {t.middles.largeGap}</span>
          )}
          {Array.isArray(item.warnings) && item.warnings.includes("LIVE_GAME") && (
            <span className="arb-warning-badge arb-warning-live" title="Trận đang live">⚡ {t.middles.liveGame}</span>
          )}
          {Array.isArray(item.warnings) && item.warnings.includes("GUARANTEED_PROFIT") && (
            <span className="arb-warning-badge arb-warning-stale" title="Middle này cũng là arb — lợi nhuận đảm bảo dù không trúng middle">✅ {t.middles.guaranteedBadge}</span>
          )}
        </div>
      )}
        </div>
      </div>
    </div>
  );
});

export default function MiddlesPage({
  active = true,
  lang = "en-GB",
  timeZone,
  currentLanguage: _currentLanguage,
  oddsFormat = "hk",
}) {
  void _currentLanguage;

  const t = useMemo(() => getAnalysisTranslations(lang), [lang]);
  const [scope, setScope] = useState("all");
  const [sport, setSport] = useState("football");
  const [pctFilter, setPctFilter] = useState(1);
  const [isPctOpen, setIsPctOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pool, setPool] = useState([]);
  const [results, setResults] = useState([]);
  const [seenBySport, setSeenBySport] = useState({
    football: new Set(),
    tennis: new Set(),
    basketball: new Set(),
    esports: new Set(),
  });

  const pollTimerRef = useRef(null);
  const inFlightRef = useRef(false);
  const abortRef = useRef(null);
  const latestRawRef = useRef([]);
  const lastWsMiddlesListRef = useRef(null);
  const lastWsMiddlesItemsRef = useRef([]);
  const lastRestLiveJsonRef = useRef(null);
  const lastRestLiveItemsRef = useRef([]);
  const lastNormalizedByKeyRef = useRef(new Map());
  const poolRef = useRef([]);
  const scopeRef = useRef(scope);
  const sportRef = useRef(sport);
  const pctFilterRef = useRef(pctFilter);
  const activeRef = useRef(active);
  const pctDropdownRef = useRef(null);
  const analysisWsRef = useRef(null);
  const analysisWsReconnectTimerRef = useRef(null);
  const analysisWsAttemptRef = useRef(0);
  const pushTransportActiveRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.__MIDDLES_DEBUG_FETCH_LIVE__ = async () => {
      const target = getMiddlesDebugTarget();
      const base = await detectAnalysisApiBase();
      const url = `${base}/api/analysis/middles/list?scope=live`;
      console.log(MIDDLES_DEBUG_PREFIX, "debug fetch start", { url, target });
      let json = null;
      try {
        const res = await fetch(url);
        const text = await res.text();
        try {
          json = JSON.parse(text);
        } catch {
          json = { _rawText: text };
        }
        console.log(MIDDLES_DEBUG_PREFIX, "REST /api/analysis/middles/list?scope=live raw", json);
      } catch (e) {
        console.log(MIDDLES_DEBUG_PREFIX, "REST debug fetch error", e);
        return;
      }
      const items = getResponseItems(json);
      const restHit = target ? items.find((it) => isMiddlesDebugTargetMatch(it, target)) : null;
      const wsItems = Array.isArray(lastWsMiddlesItemsRef.current) ? lastWsMiddlesItemsRef.current : [];
      const wsHit = target ? wsItems.find((it) => isMiddlesDebugTargetMatch(it, target)) : null;
      const normalizedMap = lastNormalizedByKeyRef.current instanceof Map ? lastNormalizedByKeyRef.current : new Map();
      const normalizedHit = target
        ? normalizedMap.get(target) || normalizedMap.get(getMiddlesDebugKeyFromItem(restHit || wsHit))
        : null;
      const lastRendered = typeof window !== "undefined" ? window.__MIDDLES_DEBUG_LAST_RENDERED__ : null;
      console.log(MIDDLES_DEBUG_PREFIX, "debug compare snapshot", {
        target,
        backend_rest_live_item: restHit || null,
        websocket_item: wsHit || null,
        normalized_ui_item: normalizedHit || null,
        last_rendered_card: lastRendered || null,
        last_ws_message: lastWsMiddlesListRef.current || null,
        last_rest_live_response: lastRestLiveJsonRef.current || null,
      });
      if (restHit) console.log(MIDDLES_DEBUG_PREFIX, "2) raw REST live item (selected)", pickMiddlesDebugFields(restHit));
      if (wsHit) console.log(MIDDLES_DEBUG_PREFIX, "1) raw websocket item (selected)", pickMiddlesDebugFields(wsHit));
      if (normalizedHit)
        console.log(MIDDLES_DEBUG_PREFIX, "3) normalized UI item (selected)", pickMiddlesDebugFields(normalizedHit));
    };
    return () => {
      try {
        delete window.__MIDDLES_DEBUG_FETCH_LIVE__;
      } catch {
        /* ignore */
      }
    };
  }, []);

  useEffect(() => {
    setPctFilter((prev) => {
      const next = clampToNearestNumberOption(prev, MIDDLE_PCT_OPTIONS);
      return next === prev ? prev : next;
    });
  }, []);

  useEffect(() => {
    scopeRef.current = scope;
    sportRef.current = sport;
    pctFilterRef.current = pctFilter;
    activeRef.current = active;
  }, [scope, sport, pctFilter, active]);

  const scopeTabs = useMemo(
    () => [
      { key: "all", label: t.common.all },
      { key: "live", label: t.common.live },
      { key: "prematch", label: t.common.prematch },
    ],
    [t]
  );

  const activeScopeIndex = useMemo(() => {
    const idx = scopeTabs.findIndex((tab) => tab.key === scope);
    return idx >= 0 ? idx : 0;
  }, [scope, scopeTabs]);

  const indicatorWidth = 100 / scopeTabs.length;

  const sportTabs = useMemo(
    () => [
      { key: "football", label: `⚽ ${t.sports.football}` },
      { key: "tennis", label: `🎾 ${t.sports.tennis}` },
      { key: "basketball", label: `🏀 ${t.sports.basketball}` },
      { key: "esports", label: `🎮 ${t.sports.esports}` },
    ],
    [t]
  );

  const sportIdsMap = useMemo(() => {
    const map = {
      football: new Set(),
      tennis: new Set(),
      basketball: new Set(),
      esports: new Set(),
    };
    pool.forEach((item) => {
      const key = getItemSport(item);
      if (map[key]) {
        const sid = getItemStableIdentityKey(item);
        if (sid) map[key].add(sid);
      }
    });
    return map;
  }, [pool]);

  const getNewCountBySport = (sportKey) => {
    const liveIds = sportIdsMap[sportKey] || new Set();
    const seenIds = seenBySport[sportKey] || new Set();
    let count = 0;
    liveIds.forEach((id) => {
      if (!seenIds.has(id)) count += 1;
    });
    return count;
  };

  const applyMiddlesPayload = useCallback((rawPayload, _debugSource) => {
    const FALLBACK_SCOPE = "prematch";
    const sourceItems = getResponseItems(rawPayload).filter(
      (item) => !(item && typeof item === "object" && item.shouldSurface === false)
    );
    const normalized = sourceItems
      .map((item) => normalizeMiddleItem(item, FALLBACK_SCOPE))
      .filter(Boolean)
      .filter((item) => getItemStableIdentityKey(item));

    const dedup = new Map();
    normalized.forEach((item) => {
      const k = getItemStableIdentityKey(item);
      if (k) dedup.set(k, item);
    });
    const fullPool = Array.from(dedup.values());

    try {
      const m = new Map();
      fullPool.forEach((it) => {
        const k = getItemStableIdentityKey(it);
        if (k) m.set(k, it);
      });
      lastNormalizedByKeyRef.current = m;
      const target = getMiddlesDebugTarget();
      if (target) {
        const hit =
          m.get(target) ||
          fullPool.find((it) => isMiddlesDebugTargetMatch(it, target)) ||
          null;
        if (hit) {
          console.log(MIDDLES_DEBUG_PREFIX, "3) normalized UI item before render list", {
            source: _debugSource || "",
            item: hit,
            picked: pickMiddlesDebugFields(hit),
          });
        }
      }
    } catch {
      /* ignore */
    }

    poolRef.current = fullPool;
    setPool(fullPool);

    const visible = filterVisibleMiddles(
      fullPool,
      sportRef.current,
      scopeRef.current,
      pctFilterRef.current
    );
    setResults(visible);
    setLoading(false);
  }, []);

  useEffect(() => {
    const visible = filterVisibleMiddles(pool, sport, scope, pctFilter);
    setResults(visible);
  }, [pool, sport, scope, pctFilter]);

  useEffect(() => {
    setSeenBySport((prev) => {
      const next = {
        football: new Set(prev.football || []),
        tennis: new Set(prev.tennis || []),
        basketball: new Set(prev.basketball || []),
        esports: new Set(prev.esports || []),
      };
      const currentSportIds = sportIdsMap[sport] || new Set();
      currentSportIds.forEach((id) => next[sport].add(id));
      Object.keys(next).forEach((key) => {
        const liveIds = sportIdsMap[key] || new Set();
        next[key] = new Set([...next[key]].filter((id) => liveIds.has(id)));
      });
      return next;
    });
  }, [sport, sportIdsMap]);

  useEffect(() => {
    if (!active) {
      pushTransportActiveRef.current = false;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      if (analysisWsReconnectTimerRef.current) {
        clearTimeout(analysisWsReconnectTimerRef.current);
        analysisWsReconnectTimerRef.current = null;
      }
      if (analysisWsRef.current) {
        try {
          analysisWsRef.current.close();
        } catch {
          /* ignore */
        }
        analysisWsRef.current = null;
      }
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const clearPoll = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    const clearWsReconnect = () => {
      if (analysisWsReconnectTimerRef.current) {
        clearTimeout(analysisWsReconnectTimerRef.current);
        analysisWsReconnectTimerRef.current = null;
      }
    };

    const scheduleWsReconnect = () => {
      clearWsReconnect();
      const attempt = analysisWsAttemptRef.current;
      const delayMs = Math.min(30000, Math.round(1000 * Math.pow(1.5, attempt)));
      analysisWsReconnectTimerRef.current = setTimeout(() => {
        analysisWsReconnectTimerRef.current = null;
        connectAnalysisWs();
      }, delayMs);
    };

    const scheduleRestPoll = () => {
      clearPoll();
      const ms = scopeRef.current === "prematch" ? 45000 : 8000;
      pollTimerRef.current = setInterval(() => {
        restTick();
      }, ms);
    };

    const restTick = async () => {
      if (!activeRef.current || inFlightRef.current) return;
      inFlightRef.current = true;
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setLoading(true);
      try {
        const base = await detectAnalysisApiBase();
        const [liveRes, earlyRes] = await Promise.all([
          fetch(`${base}/api/analysis/middles/list?scope=live`, { signal: ac.signal }),
          fetch(`${base}/api/analysis/middles/list?scope=early`, { signal: ac.signal }),
        ]);
        if (!liveRes.ok || !earlyRes.ok) throw new Error("http");
        const jl = await liveRes.json();
        const je = await earlyRes.json();
        if (!jl || jl.ok !== true || !je || je.ok !== true) throw new Error("json");
        lastRestLiveJsonRef.current = jl;
        lastRestLiveItemsRef.current = Array.isArray(jl.results) ? jl.results : [];
        try {
          const target = getMiddlesDebugTarget();
          if (target) {
            const hit = lastRestLiveItemsRef.current.find((it) => isMiddlesDebugTargetMatch(it, target));
            if (hit) {
              console.log(MIDDLES_DEBUG_PREFIX, "2) raw REST live item (from restTick)", {
                url: `${base}/api/analysis/middles/list?scope=live`,
                item: hit,
                picked: pickMiddlesDebugFields(hit),
              });
            }
          }
        } catch {
          /* ignore */
        }
        const mergedRaw = mergeRawMiddlesLists(jl.results || [], je.results || []);
        latestRawRef.current = mergedRaw;
        applyMiddlesPayload(mergedRaw, "restTick merged");
      } catch (e) {
        if (e?.name === "AbortError") return;
        if (!pushTransportActiveRef.current) applyMiddlesPayload([]);
      } finally {
        inFlightRef.current = false;
      }
    };

    const connectAnalysisWs = async () => {
      if (cancelled || !activeRef.current) return;
      const base = await detectAnalysisApiBase();
      const wsUrl = httpBaseToAnalysisWsUrl(base);
      if (!wsUrl || typeof WebSocket === "undefined") {
        pushTransportActiveRef.current = false;
        analysisWsAttemptRef.current = Math.min(analysisWsAttemptRef.current + 1, 20);
        await restTick();
        scheduleRestPoll();
        scheduleWsReconnect();
        return;
      }
      try {
        const ws = new WebSocket(wsUrl);
        analysisWsRef.current = ws;
        ws.onopen = () => {
          if (cancelled || analysisWsRef.current !== ws) return;
          analysisWsAttemptRef.current = 0;
          pushTransportActiveRef.current = true;
          clearPoll();
          clearWsReconnect();
        };
        ws.onmessage = (ev) => {
          if (cancelled || analysisWsRef.current !== ws) return;
          let msg;
          try {
            msg = JSON.parse(ev.data);
          } catch {
            return;
          }
          if (!msg || typeof msg !== "object") return;
          if (msg.type === "middles_list" && msg.ok === true) {
            const mergedRaw = Array.isArray(msg.results) ? msg.results : [];
            try {
              const firstKeys = mergedRaw.slice(0, 5).map((it) => getMiddlesDebugKeyFromItem(it)).filter(Boolean);
              console.log(MIDDLES_DEBUG_PREFIX, "WS message", {
                type: msg.type,
                count: mergedRaw.length,
                first5: firstKeys,
              });
              lastWsMiddlesListRef.current = msg;
              lastWsMiddlesItemsRef.current = mergedRaw;
              const target = getMiddlesDebugTarget();
              if (target) {
                const hit = mergedRaw.find((it) => isMiddlesDebugTargetMatch(it, target));
                if (hit) {
                  console.log(MIDDLES_DEBUG_PREFIX, "1) raw websocket item (from middles_list)", {
                    item: hit,
                    picked: pickMiddlesDebugFields(hit),
                  });
                }
              }
            } catch {
              /* ignore */
            }
            latestRawRef.current = mergedRaw;
            applyMiddlesPayload(mergedRaw, "ws middles_list");
          }
        };
        ws.onerror = () => {
          try {
            ws.close();
          } catch {
            /* ignore */
          }
        };
        ws.onclose = () => {
          if (analysisWsRef.current === ws) analysisWsRef.current = null;
          if (cancelled) return;
          pushTransportActiveRef.current = false;
          analysisWsAttemptRef.current = Math.min(analysisWsAttemptRef.current + 1, 20);
          scheduleRestPoll();
          scheduleWsReconnect();
        };
      } catch {
        pushTransportActiveRef.current = false;
        analysisWsAttemptRef.current = Math.min(analysisWsAttemptRef.current + 1, 20);
        restTick();
        scheduleRestPoll();
        scheduleWsReconnect();
      }
    };

    (async () => {
      await restTick();
      if (cancelled) return;
      await connectAnalysisWs();
    })();

    return () => {
      cancelled = true;
      clearPoll();
      clearWsReconnect();
      pushTransportActiveRef.current = false;
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      if (analysisWsRef.current) {
        try {
          analysisWsRef.current.close();
        } catch {
          /* ignore */
        }
        analysisWsRef.current = null;
      }
    };
  }, [active, applyMiddlesPayload, scope]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pctDropdownRef.current && !pctDropdownRef.current.contains(e.target)) {
        setIsPctOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setIsPctOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, { passive: true });
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const page = {
    padding: "10px",
    paddingTop: 4,
    color: "#f4f7ff",
    boxSizing: "border-box",
  };

  const topbar = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 8,
  };

  const segment = {
    position: "relative",
    display: "flex",
    borderRadius: 12,
    background: "linear-gradient(180deg, #101c3b 0%, #0b1630 100%)",
    border: "1px solid rgba(180, 197, 235, 0.10)",
    boxShadow: "inset 0 1px 0 rgba(180, 197, 235, 0.06), 0 4px 16px rgba(0, 0, 0, 0.32)",
    overflow: "hidden",
    flex: "1 1 auto",
    height: 40,
    minWidth: 0,
  };

  const segBtn = (isActive) => ({
    flex: 1,
    border: "none",
    background: "transparent",
    color: isActive ? "#f4f7ff" : "#a8b3cf",
    fontWeight: 600,
    cursor: "pointer",
    fontSize: 12,
    height: "100%",
    zIndex: 2,
  });

  const selectWrap = { position: "relative", height: 40, minWidth: 84, flexShrink: 0 };

  const selectBtn = {
    position: "relative",
    width: "100%",
    height: "100%",
    padding: "0 30px 0 14px",
    borderRadius: 12,
    border: `1px solid ${isPctOpen ? "rgba(76, 111, 255, 0.45)" : "rgba(180, 197, 235, 0.10)"}`,
    background: "linear-gradient(180deg, #152349 0%, #101c3b 100%)",
    color: "#f4f7ff",
    outline: "none",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    textAlign: "left",
    boxShadow: isPctOpen ? "0 0 0 1px rgba(76, 111, 255, 0.15), inset 0 1px 0 rgba(180, 197, 235, 0.06)" : "inset 0 1px 0 rgba(180, 197, 235, 0.06), 0 4px 16px rgba(0, 0, 0, 0.32)",
  };

  const sportScroll = {
    display: "flex",
    alignItems: "stretch",
    gap: 0,
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    borderRadius: 12,
    background: "linear-gradient(180deg, #101c3b 0%, #0b1630 100%)",
    border: "1px solid rgba(180, 197, 235, 0.10)",
    boxShadow: "inset 0 1px 0 rgba(180, 197, 235, 0.06)",
  };

  const sportBtn = (isActive) => ({
    position: "relative",
    flex: "0 0 auto",
    border: "none",
    background: "transparent",
    color: isActive ? "#f4f7ff" : "#a8b3cf",
    fontWeight: 600,
    cursor: "pointer",
    fontSize: 12,
    height: 40,
    padding: "0 14px",
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    boxShadow: isActive ? "inset 0 -3px 0 #4c6fff" : "none",
  });

  const emptyBox = {
    textAlign: "center",
    padding: "28px 14px",
    color: "#a8b3cf",
    fontWeight: 600,
    fontSize: 12,
    marginTop: 10,
    borderRadius: 14,
    border: "1px dashed rgba(180, 197, 235, 0.16)",
    background: "rgba(17, 31, 66, 0.55)",
  };

  return (
    <div style={page}>
      <div style={topbar}>
        <div style={segment}>
          {scopeTabs.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setScope(item.key)}
              style={segBtn(scope === item.key)}
            >
              {item.label}
            </button>
          ))}
          <div
            style={{
              position: "absolute",
              bottom: 0,
              height: 3,
              background: "#4c6fff",
              transition: "left 0.25s ease",
              zIndex: 1,
              width: `${indicatorWidth}%`,
              left: `${indicatorWidth * activeScopeIndex}%`,
            }}
          />
        </div>

        <div ref={pctDropdownRef} style={selectWrap}>
          <button
            type="button"
            onClick={() => setIsPctOpen((v) => !v)}
            style={selectBtn}
            aria-haspopup="listbox"
            aria-expanded={isPctOpen}
          >
            <span style={{ display: "block", lineHeight: "38px" }}>≥{pctFilter}%</span>
            <span
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                color: "#7d89a8",
                fontSize: 11,
                pointerEvents: "none",
                lineHeight: 1,
                transform: `translateY(-50%) rotate(${isPctOpen ? 180 : 0}deg)`,
                transition: "transform 0.2s ease",
              }}
            >
              ▾
            </span>
          </button>
          {isPctOpen ? (
            <div
              role="listbox"
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                left: 0,
                right: 0,
                padding: 6,
                borderRadius: 12,
                background: "linear-gradient(180deg, #111f42 0%, #0b1630 100%)",
                border: "1px solid rgba(180, 197, 235, 0.10)",
                boxShadow: "0 14px 32px rgba(0,0,0,0.32)",
                zIndex: 50,
              }}
            >
              {MIDDLE_PCT_OPTIONS.map((value) => {
                const activeOption = pctFilter === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setPctFilter(value);
                      setIsPctOpen(false);
                    }}
                    style={{
                      width: "100%",
                      border: "none",
                      background: activeOption ? "rgba(76, 111, 255, 0.18)" : "transparent",
                      color: activeOption ? "#f4f7ff" : "#a8b3cf",
                      textAlign: "left",
                      padding: "10px 10px",
                      borderRadius: 9,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    ≥{value}%
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={sportScroll} role="tablist" aria-label={t.common.filter}>
          {sportTabs.map((item) => {
            const isActive = sport === item.key;
            const newCount = getNewCountBySport(item.key);
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setSport(item.key)}
                style={sportBtn(isActive)}
              >
                <span>{item.label}</span>
                {!isActive && newCount > 0 ? (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 16,
                      height: 16,
                      padding: "0 4px",
                      borderRadius: 999,
                      background: "#ef4444",
                      color: "#fff",
                      fontSize: 9,
                      fontWeight: 800,
                      lineHeight: 1,
                    }}
                  >
                    +{newCount}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        {loading && results.length === 0 ? (
          <div style={emptyBox}>{t.arbitrage.scanning}</div>
        ) : results.length === 0 ? (
          <div style={emptyBox}>{t.common.noData}</div>
        ) : (
          <div className="arb-list">
            {results.map((m) => (
              <MiddleCard
                key={m.id ?? m.key}
                item={m}
                lang={lang}
                t={t}
                timeZone={timeZone}
                oddsFormat={oddsFormat}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
